import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { fetchClaimsPage } from '@/features/claims/api/api'
import { requireAuthToken } from '@/lib/api'
import { claimsKeys } from '@/lib/queryKeys'

const CLAIM_HYDRATION_PAGE_LIMIT = 1

export function useClaimHydrationQuery(
  claimId: string | null | undefined,
  shouldHydrate: boolean
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: claimsKeys.hydration(organization?.id, claimId),
    enabled: Boolean(organization?.id && claimId && shouldHydrate),
    queryFn: async ({ signal }) => {
      if (!claimId) throw new Error('claimId is required')
      const token = await requireAuthToken(getToken)
      const page = await fetchClaimsPage({
        token,
        params: {
          limit: CLAIM_HYDRATION_PAGE_LIMIT,
          filters: [
            {
              id: 'claim-id-hydration',
              field: 'id',
              operator: '=',
              value: claimId,
            },
          ],
        },
        signal,
      })
      const claim = page.data[0] ?? null
      if (claim !== null && claim.id !== claimId) {
        throw new Error('Invalid claims response: exact ID did not match')
      }
      return claim
    },
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
